import React, { useState, useEffect, useRef } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import { AuthModal } from './AuthComponents';

// Main Map Component
function MapComponent() {
  const { user, token, isAuthenticated, logout } = useAuth();
  
  console.log('=== MapComponent RENDERED - Seating Version 1.0 ===');
  
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFeature, setEditedFeature] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 });
  const [mapZoom, setMapZoom] = useState(13);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const isDraggingMarkerRef = useRef(false);
  const markersRef = useRef([]);
  const isEditingRef = useRef(false);
  const editedFeatureRef = useRef(null);

  const API_BASE = 'http://174.138.79.56:5050';

  // Initialize Leaflet map
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.3/dist/leaflet.css';
    link.integrity = 'sha256-kLaT2GOSpHechhsozzB+flnD+zUyjE2LlfWPgU04xyI=';
    link.crossOrigin = '';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.3/dist/leaflet.js';
    script.integrity = 'sha256-WBkoXOwTeyKclOHuWtc+i2uENFpDZ9YPdf5Hf+D7ewM=';
    script.crossOrigin = '';
    script.async = true;

    script.onload = () => {
      const L = window.L;
      
      const map = L.map(mapRef.current, {
        maxZoom: 22,
        minZoom: 10
      }).setView([mapCenter.lat, mapCenter.lng], mapZoom);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 22,
        maxNativeZoom: 19
      }).addTo(map);

      markersLayerRef.current = L.layerGroup().addTo(map);
      leafletMapRef.current = map;

      fetchMarkers(map.getBounds());

      map.on('moveend', () => {
        fetchMarkers(map.getBounds());
      });

      map.on('click', (e) => {
        if (selectedFeature && !isDraggingMarkerRef.current) {
          setSelectedFeature(null);
          setEditedFeature(null);
          setIsEditing(false);
        }
        isDraggingMarkerRef.current = false;
      });
    };

    document.head.appendChild(script);

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
      }
    };
  }, []);

  const markerObjectsRef = useRef(new Map());
  const isAuthenticatedRef = useRef(isAuthenticated);

  // Keep isAuthenticatedRef in sync
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Keep markersRef in sync with markers state
  useEffect(() => {
    markersRef.current = markers;
  }, [markers]);

  // Keep refs in sync with editing state
  useEffect(() => {
    isEditingRef.current = isEditing;
    editedFeatureRef.current = editedFeature;
  }, [isEditing, editedFeature]);

  // Rebuild all markers when state changes
  useEffect(() => {
    if (!leafletMapRef.current || !markersLayerRef.current || !window.L) return;

    const L = window.L;
    
    // Clear ALL existing markers
    markerObjectsRef.current.forEach((marker, key) => {
      markersLayerRef.current.removeLayer(marker);
    });
    markerObjectsRef.current.clear();

    // Recreate all markers from scratch with fresh data
    markers.forEach(marker => {
      const isSelected = selectedFeature && selectedFeature.siteid === marker.siteid;
      const radius = isSelected ? 16 : 10;
      const color = isSelected ? '#FF0000' : '#00008B'; 
      
      let position;
      if (isSelected && editedFeature) {
        position = [editedFeature.latitude, editedFeature.longitude];
      } else {
        position = [marker.latitude, marker.longitude];
      }

      const leafletMarker = L.circleMarker(position, {
        radius: radius,
        color: 'white',
        fillColor: color,
        fillOpacity: 0.9,
        weight: isSelected ? 4 : 3,
        opacity: 1,
        bubblingMouseEvents: false
      }).addTo(markersLayerRef.current);

      const hitArea = L.circleMarker(position, {
        radius: 30,
        fillOpacity: 0,
        opacity: 0,
        bubblingMouseEvents: false,
        interactive: true,
        className: isSelected && isEditing ? 'draggable-marker' : ''
      }).addTo(markersLayerRef.current);

      markerObjectsRef.current.set(marker.siteid, leafletMarker);
      markerObjectsRef.current.set(`${marker.siteid}_hit`, hitArea);

      const clickHandler = (function(siteId) {
        return function(e) {
          L.DomEvent.stopPropagation(e);
          if (!isDraggingMarkerRef.current) {
            const currentMarker = markersRef.current.find(m => m.siteid === siteId);
            console.log('Click handler - siteid:', siteId);
            console.log('Click handler - found marker:', currentMarker);
            if (currentMarker) {
              handleMarkerClick(currentMarker);
            }
          }
        };
      })(marker.siteid);

      if (isSelected && isEditing) {
        // In edit mode - make draggable, no click handler
        if (leafletMarker.getElement()) {
          leafletMarker.getElement().style.cursor = 'move';
        }
        // Set cursor on hit area too
        setTimeout(() => {
          const hitAreaElement = hitArea._path || hitArea.getElement();
          if (hitAreaElement) {
            hitAreaElement.style.cursor = 'move';
          }
        }, 0);
        
        const dragHandler = function(e) {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          
          isDraggingMarkerRef.current = true;
          const map = leafletMapRef.current;
          map.dragging.disable();
          
          const onMouseMove = function(e) {
            const newLatLng = e.latlng;
            leafletMarker.setLatLng(newLatLng);
            if (hitArea) {
              hitArea.setLatLng(newLatLng);
            }
          };
          
          const onMouseUp = function(e) {
            map.dragging.enable();
            map.off('mousemove', onMouseMove);
            map.off('mouseup', onMouseUp);
            
            const newPos = leafletMarker.getLatLng();
            handleMarkerDrag(newPos);
            
            setTimeout(() => {
              isDraggingMarkerRef.current = false;
            }, 100);
          };
          
          map.on('mousemove', onMouseMove);
          map.on('mouseup', onMouseUp);
        };
        
        leafletMarker.on('mousedown', dragHandler);
        hitArea.on('mousedown', dragHandler);
      } else {
        // Not in edit mode - clickable
        leafletMarker.on('click', clickHandler);
        hitArea.on('click', clickHandler);
        
        if (leafletMarker.getElement()) {
          leafletMarker.getElement().style.cursor = 'pointer';
        }
      }
    });
  }, [markers, selectedFeature, editedFeature, isEditing]);

  const fetchMarkers = async (bounds) => {
    // Block data fetching for non-authenticated users using ref
    if (!isAuthenticatedRef.current) {
      console.log('User not authenticated, skipping fetch');
      setMarkers([]);
      setLoading(false);
      return;
    }
    
    console.log('User authenticated, proceeding with fetch');
    
    const currentZoom = leafletMapRef.current?.getZoom();
    const MIN_ZOOM_LEVEL = 16;
    
    if (!currentZoom || currentZoom < MIN_ZOOM_LEVEL) {
      console.log('Zoom level too low:', currentZoom);
      setMarkers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log('Fetching markers for bounds:', bounds);
    try {
      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();
      
      const response = await fetch(
        `${API_BASE}/api/markers?lat1=${southWest.lat}&lng1=${southWest.lng}&lat2=${northEast.lat}&lng2=${northEast.lng}`
      );
      const data = await response.json();
      
      console.log('Fetched', data.length, 'markers');
      
      // Use refs to get current editing state
      const currentEditedFeature = editedFeatureRef.current;
      const currentIsEditing = isEditingRef.current;
      
      console.log('Fetch - isEditing:', currentIsEditing, 'editedFeature:', currentEditedFeature?.siteid);
      
      if (currentIsEditing && currentEditedFeature) {
        // Remove the marker at its old position from the fetched data
        const filteredData = data.filter(m => m.siteid !== currentEditedFeature.siteid);
        // Add the edited marker at its new position
        console.log('Preserving edited marker at:', currentEditedFeature.latitude, currentEditedFeature.longitude);
        setMarkers([...filteredData, currentEditedFeature]);
      } else if (currentEditedFeature && !currentIsEditing) {
        // Selected but not editing - still preserve any changes
        const updatedData = data.map(marker => 
          marker.siteid === currentEditedFeature.siteid ? currentEditedFeature : marker
        );
        setMarkers(updatedData);
      } else {
        setMarkers(data);
      }
    } catch (error) {
      console.error('Error fetching markers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update map interaction based on authentication status
  useEffect(() => {
    if (!leafletMapRef.current) return;
    
    const map = leafletMapRef.current;
    
    if (isAuthenticated) {
      map.dragging.enable();
      map.scrollWheelZoom.enable();
      map.doubleClickZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      map.touchZoom.enable();
      
      // Fetch markers after authentication
      console.log('User logged in, fetching markers...');
      fetchMarkers(map.getBounds());
    } else {
      map.dragging.disable();
      map.scrollWheelZoom.disable();
      map.doubleClickZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
      map.touchZoom.disable();
      
      // Clear markers when logged out
      setMarkers([]);
    }
  }, [isAuthenticated]);

  const handleMarkerClick = (clickedFeature) => {
    console.log('Clicked feature:', clickedFeature);
    console.log('Current markers state:', markers);
    const latestFeature = markers.find(m => m.siteid === clickedFeature.siteid) || clickedFeature;
    console.log('Latest feature from state:', latestFeature);
    console.log('Latest lat/lng:', latestFeature.latitude, latestFeature.longitude);
    setSelectedFeature(latestFeature);
    setEditedFeature({ ...latestFeature });
    setIsEditing(false);
  };

  const handleEdit = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setIsEditing(true);
  };

  const handleAttributeChange = (field, value) => {
    setEditedFeature(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMarkerDrag = (newPos) => {
    setEditedFeature(prev => ({
      ...prev,
      latitude: newPos.lat,
      longitude: newPos.lng
    }));
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      alert('You must be logged in to save changes');
      setShowAuthModal(true);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/updateSite/${editedFeature.siteid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nearest_add: editedFeature.nearest_add,
          category: editedFeature.category,
          latitude: editedFeature.latitude,
          longitude: editedFeature.longitude
        }),
      });

      if (response.ok) {
        const updatedFeature = await response.json();
        
        console.log('Server response:', updatedFeature);
        console.log('Edited feature before merge:', editedFeature);
        
        const finalFeature = {
          ...updatedFeature,
          latitude: parseFloat(editedFeature.latitude),
          longitude: parseFloat(editedFeature.longitude),
          nearest_add: editedFeature.nearest_add,
          category: editedFeature.category
        };
        
        console.log('Final feature to save:', finalFeature);
        console.log('Final lat/lng:', finalFeature.latitude, finalFeature.longitude);
        
        setMarkers(prev => {
          const updated = prev.map(m => 
            m.siteid === finalFeature.siteid ? finalFeature : m
          );
          console.log('Updated markers array:', updated);
          console.log('Updated marker for this site:', updated.find(m => m.siteid === finalFeature.siteid));
          return updated;
        });
        
        setSelectedFeature(finalFeature);
        setEditedFeature(finalFeature);
        setIsEditing(false);
        
        alert('Feature updated successfully!');
      } else {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        alert('Error updating feature: ' + errorText);
      }
    } catch (error) {
      console.error('Error saving feature:', error);
      alert('Error saving feature: ' + error.message);
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      setEditedFeature({ ...selectedFeature });
      setIsEditing(false);
    } else {
      setSelectedFeature(null);
      setEditedFeature(null);
      setIsEditing(false);
    }
  };

  const handleExportCSV = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/exportAll`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      
      const data = await response.json();
      
      if (!data || data.length === 0) {
        alert('No data to export');
        return;
      }
      
      // Get all keys from the first object to create headers
      const headers = Object.keys(data[0]);
      
      // Create CSV content
      const csvContent = [
        headers.join(','), // Header row
        ...data.map(row => 
          headers.map(header => {
            const value = row[header];
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',')
        )
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('href', url);
      link.setAttribute('download', `seating_export_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert(`Successfully exported ${data.length} records`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Error exporting data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', margin: 0, padding: 0 }}>
      <div 
        ref={mapRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0
        }}
      />

      <div style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        background: 'white',
        padding: '10px 15px',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <div style={{ fontSize: '10px', color: '#9C27B0', marginRight: '10px', fontWeight: 'bold' }}>SEATING v1.0</div>
        {isAuthenticated && (
          <button
            onClick={handleExportCSV}
            disabled={loading}
            style={{
              padding: '6px 12px',
              background: loading ? '#ccc' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 'bold'
            }}
          >
            {loading ? 'Exporting...' : 'Export CSV'}
          </button>
        )}
        {isAuthenticated ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#4CAF50'
              }} />
              <span style={{ fontWeight: 'bold', color: '#333' }}>{user.username}</span>
            </div>
            <button
              onClick={logout}
              style={{
                padding: '6px 12px',
                background: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold'
              }}
            >
              Logout
            </button>
          </>
        ) : (
          <button
            onClick={() => setShowAuthModal(true)}
            style={{
              padding: '8px 16px',
              background: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            Login / Register
          </button>
        )}
      </div>

      {loading && (
        <div style={{
          position: 'absolute',
          top: '60px',
          left: '10px',
          background: 'white',
          padding: '10px 15px',
          borderRadius: '4px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          zIndex: 1000
        }}>
          Loading markers...
        </div>
      )}

      {leafletMapRef.current && leafletMapRef.current.getZoom() < 16 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '20px 30px',
          borderRadius: '8px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          zIndex: 1000,
          textAlign: 'center',
          maxWidth: '300px'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
            Zoom in to view markers
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Please zoom in to street level (zoom 16+) to load seating locations
          </div>
        </div>
      )}

      {editedFeature && (
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          width: '350px',
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000,
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>
            Seating Details
          </h3>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
              Site ID
            </label>
            <input
              type="text"
              value={editedFeature.siteid || ''}
              disabled
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: '#f5f5f5',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
              Nearest Address
            </label>
            <input
              type="text"
              value={editedFeature.nearest_add || ''}
              disabled={!isEditing}
              onChange={(e) => handleAttributeChange('nearest_add', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: isEditing ? 'white' : '#f5f5f5',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
              Category
            </label>
            <input
              type="text"
              value={editedFeature.category || ''}
              disabled={!isEditing}
              onChange={(e) => handleAttributeChange('category', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: isEditing ? 'white' : '#f5f5f5',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
              On Street
            </label>
            <input
              type="text"
              value={editedFeature.on_street || ''}
              disabled
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: '#f5f5f5',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
              Borough
            </label>
            <input
              type="text"
              value={editedFeature.boroname || ''}
              disabled
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: '#f5f5f5',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
              Latitude
            </label>
            <input
              type="text"
              value={editedFeature.latitude?.toFixed(6) || ''}
              disabled
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: isEditing ? '#ffffcc' : '#f5f5f5',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
              Longitude
            </label>
            <input
              type="text"
              value={editedFeature.longitude?.toFixed(6) || ''}
              disabled
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: isEditing ? '#ffffcc' : '#f5f5f5',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {!isAuthenticated && !isEditing && (
            <div style={{
              padding: '12px',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              marginBottom: '15px',
              fontSize: '13px',
              color: '#856404'
            }}>
              ⚠️ You must be logged in to edit features
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            {!isEditing ? (
              <>
                <button
                  onClick={handleEdit}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={handleCancel}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#757575',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {!isAuthenticated && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 999,
          pointerEvents: 'all'
        }} />
      )}

      {!isAuthenticated && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 255, 255, 0.98)',
          padding: '30px 40px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          zIndex: 1001,
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '15px' }}>🔒</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', color: '#333' }}>
            Login Required
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
            Please log in to interact with the map and view seating locations
          </div>
          <button
            onClick={() => setShowAuthModal(true)}
            style={{
              padding: '12px 24px',
              background: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            Login / Register
          </button>
        </div>
      )}

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MapComponent />
    </AuthProvider>
  );
}